import React, { useState, useCallback, useEffect } from "react";
import { putContributor, deleteContributor } from "../services/AnimalSplitCore";
import SelectContributorIcon from "../components/SelectContributorIcon";
import VenmoUsernameSection from "../components/VenmoUsernameSection";
import ZellePaymentSection from "../components/ZellePaymentSection";
import { toggleModal } from "../utils/utils";
import { getCookie, setCookie } from "../utils/cookies";
import { isVenmoUsernameCapitalizationValid } from "../utils/venmo";
import { MODAL_ID } from "../constants";
import { useTranslation } from "react-i18next";
import AnimalEasterEggBadge from "../components/AnimalEasterEggBadge";
import "./ModalTheme.css";

const VENMO_USERNAME_COOKIE = "as:venmoUsername";
const ZELLE_USERNAME_COOKIE = "as:zelleUsername";
const ZELLE_RECIPIENT_COOKIE = "as:zelleRecipientName";

export default function EditContributor(props) {
  const { t } = useTranslation();
  const { modalId, groupId, contributor, renderGroup, setIsLoading, isSettled = false } = props;
  const { id: contributorId } = contributor;

  const [name, setName] = useState(contributor.name);
  const [contributorIconId, setContributorIconId] = useState(contributor.icon);
  const [venmoUsername, setVenmoUsername] = useState(
    contributor.venmoUsername || ""
  );
  const [showVenmoSection, setShowVenmoSection] = useState(
    !!(contributor.venmoUsername && String(contributor.venmoUsername).trim())
  );
  const [venmoResetSignal, setVenmoResetSignal] = useState(0);
  const [showZelleSection, setShowZelleSection] = useState(
    !!(
      contributor.zelleUsername?.trim() &&
      contributor.zelleRecipientName?.trim()
    )
  );
  const [zelleUsername, setZelleUsername] = useState(contributor.zelleUsername || "");
  const [zelleRecipientName, setZelleRecipientName] = useState(
    contributor.zelleRecipientName || ""
  );
  const [formValidated, setFormValidated] = useState(false);
  const [isVenmoValid, setIsVenmoValid] = useState(true);
  const [isZelleValid, setIsZelleValid] = useState(true);

  const handleModalWillShow = useCallback(() => {
    const remembered = getCookie(VENMO_USERNAME_COOKIE) || "";
    const rememberedZelleUser = getCookie(ZELLE_USERNAME_COOKIE) || "";
    const rememberedZelleName = getCookie(ZELLE_RECIPIENT_COOKIE) || "";
    const hasContributorVenmo = !!(contributor.venmoUsername && String(contributor.venmoUsername).trim());
    const hasContributorZelle = !!(
      contributor.zelleUsername?.trim() && contributor.zelleRecipientName?.trim()
    );
    // Prefill from cookie, but DO NOT auto-enable the toggle unless this contributor already has Venmo set.
    const initialVenmoUsername = contributor.venmoUsername || remembered || "";
    setFormValidated(false);
    setVenmoUsername(initialVenmoUsername);
    setShowVenmoSection(hasContributorVenmo);
    setVenmoResetSignal((v) => v + 1);
    setIsVenmoValid(true); // Reset to valid state, will be updated by validation effect
    setZelleUsername(
      contributor.zelleUsername || rememberedZelleUser || ""
    );
    setZelleRecipientName(
      contributor.zelleRecipientName || rememberedZelleName || ""
    );
    setShowZelleSection(hasContributorZelle);
    setIsZelleValid(true);
  }, [
    setFormValidated,
    contributor.venmoUsername,
    contributor.zelleUsername,
    contributor.zelleRecipientName,
  ]);

  useEffect(() => {
    const editContributorCreationModal = document.getElementById(modalId);
    if (!editContributorCreationModal) return;
    editContributorCreationModal.addEventListener(
      "show.bs.modal",
      handleModalWillShow
    );

    return () => {
      editContributorCreationModal.removeEventListener(
        "show.bs.modal",
        handleModalWillShow
      );
    };
  }, [modalId, handleModalWillShow]);

  const handleUpdate = async () => {
    if (!isFormValid()) {
      setFormValidated(true);
      return;
    }

    try {
      setIsLoading?.(true);
      const normalizedVenmoUsername = venmoUsername.trim().replace(/^@/, "");
      // Hard-block invalid capitalization on save (avoid stale validation state)
      if (
        showVenmoSection &&
        normalizedVenmoUsername &&
        !isVenmoUsernameCapitalizationValid(normalizedVenmoUsername)
      ) {
        setIsLoading?.(false);
        setFormValidated(true);
        return;
      }
      const normalizedZelleUsername = zelleUsername.trim();
      const normalizedZelleRecipientName = zelleRecipientName.trim();
      const updatePayload = {
        icon: contributorIconId,
        venmoUsername: showVenmoSection ? normalizedVenmoUsername || null : null,
        zelleUsername: showZelleSection ? normalizedZelleUsername || null : null,
        zelleRecipientName: showZelleSection ? normalizedZelleRecipientName || null : null,
      };
      // Settled groups: keep contributor name locked.
      if (!isSettled) updatePayload.name = name;

      const { payload } = await putContributor(groupId, contributorId, updatePayload);
      setIsLoading?.(false);
      renderGroup(payload);
      if (showVenmoSection && normalizedVenmoUsername) {
        setCookie(VENMO_USERNAME_COOKIE, normalizedVenmoUsername);
      }
      if (showZelleSection && normalizedZelleUsername && normalizedZelleRecipientName) {
        setCookie(ZELLE_USERNAME_COOKIE, normalizedZelleUsername);
        setCookie(ZELLE_RECIPIENT_COOKIE, normalizedZelleRecipientName);
      }
    } catch (error) {
      setIsLoading?.(false);
      console.error(error);
      toggleModal(MODAL_ID.CONNECTION_ERROR);
    }
  };

  const handleDelete = async () => {
    try {
      setIsLoading?.(true);
      const { payload } = await deleteContributor(groupId, contributorId);
      setIsLoading?.(false);
      renderGroup(payload);
    } catch (error) {
      setIsLoading?.(false);
      console.error(error);
      toggleModal(MODAL_ID.CONNECTION_ERROR);
    }
  };

  const isFormValid = () => {
    if (name === "") return false;
    // If Venmo section is enabled, validate the username
    if (showVenmoSection && !isVenmoValid) return false;
    if (showZelleSection && !isZelleValid) return false;
    return true;
  };

  return (
    <div className="modal fade" id={modalId} tabIndex="-1">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content as-modal as-theme">
          <div className="modal-header">
            <div className="as-modal-header-row">
              <div className="as-modal-title-row">
                <span className="as-modal-icon-badge" aria-hidden="true">
                  🐾
                </span>
                <h5 className="modal-title" id="exampleModalLabel">
                  {t("participant.edit_participant")}
                </h5>
              </div>
              <div className="as-modal-actions">
                <AnimalEasterEggBadge seed={`${groupId}:edit-contributor:${contributorId}`} />
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                ></button>
              </div>
            </div>
          </div>

          <div className={`modal-body ${formValidated ? "was-validated" : ""}`}>
            {isSettled ? (
              <div className="alert alert-warning py-2">
                {t("group.settled") || "Settled"} —{" "}
                {t("participant.settled_name_locked_hint")}
              </div>
            ) : null}
            <label className="form-label as-field-label">
              <span className="as-field-label-badge" aria-hidden="true">🐾</span>
              <span className="as-field-label-text">{t("participant.participant_name")}</span>
            </label>
            <div className="d-flex">
              <input
                type="text"
                className="form-control me-2 as-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={isSettled}
              />
              <SelectContributorIcon
                setContributorIconId={setContributorIconId}
                contributorIconId={contributorIconId}
              />
            </div>

            <VenmoUsernameSection
              idBase={`${modalId}-venmo`}
              modalId={modalId}
              enabled={showVenmoSection}
              onEnabledChange={setShowVenmoSection}
              venmoUsername={venmoUsername}
              setVenmoUsername={setVenmoUsername}
              resetSignal={venmoResetSignal}
              onValidationChange={setIsVenmoValid}
            />

            <ZellePaymentSection
              idBase={`${modalId}-zelle`}
              enabled={showZelleSection}
              onEnabledChange={setShowZelleSection}
              zelleUsername={zelleUsername}
              setZelleUsername={setZelleUsername}
              zelleRecipientName={zelleRecipientName}
              setZelleRecipientName={setZelleRecipientName}
              onValidationChange={setIsZelleValid}
            />
          </div>

          <div className="modal-footer as-modal-footer">
            <button
              type="button"
              className="btn as-btn-soft"
              data-bs-dismiss="modal"
            >
              {t("general.close")}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              data-bs-dismiss="modal"
              onClick={handleDelete}
              disabled={isSettled}
            >
              {t("general.delete")}
            </button>
            <button
              type="button"
              className="btn btn-primary as-btn-primary-gradient"
              data-bs-dismiss={`${isFormValid() ? "modal" : ""}`}
              onClick={handleUpdate}
              disabled={!isFormValid()}
            >
              {t("general.save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
